/**
 * useCommandBar Hook
 * Manages universal command bar state, parsing, and execution
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../../auth/AuthProviderUnified';
import { useJobsites } from '../../../hooks/useJobsites';
import { useWorkers } from '../../../hooks/useWorkers';
import { fetchGlobalSearch } from '../../../services/api/search';
import logger from '../../../utils/logger';
import {
  COMMAND_TEMPLATES,
  COMMAND_QUICK_ACTIONS,
} from '../CommandBar/commandBarConfig';
import { flattenVisibleSearchResults } from '../CommandBar/searchResultNavigation';

const RECENT_STORAGE_KEY = 'vh_command_bar_recent';

// Command type detection patterns
const ACTION_VERBS = ['send', 'schedule', 'create', 'generate', 'notify', 'assign', 'update', 'add', 'remove'];
const QUERY_VERBS = ['show', 'find', 'list', 'view', 'search', 'compare', 'get', 'check'];

/**
 * Detect if input is a command or search query
 */
const detectInputType = (input) => {
  const lowerInput = input.toLowerCase().trim();
  const firstWord = lowerInput.split(' ')[0];

  if (ACTION_VERBS.includes(firstWord)) {
    return 'command';
  }
  if (QUERY_VERBS.includes(firstWord)) {
    return 'query';
  }
  return 'search';
};

/**
 * Parse command into structured format
 */
const parseCommand = (input, templates) => {
  const lowerInput = input.toLowerCase().trim();
  const words = lowerInput.split(' ');

  // Try to match against templates
  for (const template of templates) {
    const pattern = template.pattern.toLowerCase();
    const keywords = template.keywords || [];
    const matchesKeyword = keywords.some((keyword) => lowerInput.includes(keyword));
    const matchesPattern = words.some((word) => pattern.includes(word));
    if (matchesKeyword || matchesPattern) {
      return {
        template,
        parsed: {
          action: words[0],
          targets: words.slice(1),
        },
        confidence: 0.85,
      };
    }
  }

  return {
    template: null,
    parsed: {
      action: words[0],
      targets: words.slice(1),
    },
    confidence: 0.5,
  };
};

/**
 * Generate suggestions based on input
 */
const generateSuggestions = (input, recentCommands, templates, entities) => {
  const lowerInput = input.toLowerCase().trim();
  const suggestions = {
    templates: [],
    entities: [],
    recent: [],
  };

  if (!lowerInput) {
    // Return default suggestions when empty
    suggestions.templates = templates.slice(0, 4);
    suggestions.recent = recentCommands.slice(0, 3);
    return suggestions;
  }

  // Match templates
  suggestions.templates = templates.filter((t) =>
    t.pattern.toLowerCase().includes(lowerInput) ||
    t.description.toLowerCase().includes(lowerInput)
  ).slice(0, 4);

  // Match entities
  const allEntities = [
    ...entities.workers.map((e) => ({ ...e, type: 'worker' })),
    ...entities.projects.map((e) => ({ ...e, type: 'project' })),
    ...entities.crews.map((e) => ({ ...e, type: 'crew' })),
    ...entities.sites.map((e) => ({ ...e, type: 'site' })),
  ];
  suggestions.entities = allEntities
    .filter((e) => e.name.toLowerCase().includes(lowerInput))
    .slice(0, 5);

  // Match recent commands
  suggestions.recent = recentCommands
    .filter((c) => c.query.toLowerCase().includes(lowerInput))
    .slice(0, 3);

  return suggestions;
};

/**
 * Main hook for Command Bar functionality
 */
const safeParseStoredCommands = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(RECENT_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const persistRecentCommands = (commands) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(commands.slice(0, 20)));
  } catch (error) {
    logger.warn('[CommandBar] Failed to persist recent commands', { error: error.message });
  }
};

const buildPreview = (command, parsed) => {
  if (!parsed?.template) {
    return {
      type: 'info',
      title: 'Review command before continuing',
      details: {
        message: command,
      },
      canExecute: false,
      notice: 'Command execution is not wired to production yet.',
    };
  }

  if (parsed.template.action) {
    return {
      type: 'action_preview',
      title: parsed.template.description,
      details: {
        message: command,
      },
      canExecute: false,
      notice: 'Command execution is not wired to production yet.',
    };
  }

  return {
    type: 'info',
    title: parsed.template.description || 'Command ready',
    details: {
      message: command,
    },
    canExecute: false,
    notice: 'Command execution is not wired to production yet.',
  };
};

export const useCommandBar = ({ onNavigate } = {}) => {
  const { user } = useAuth();

  // State
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [inputType, setInputType] = useState('search');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [results, setResults] = useState(null);
  const [history, setHistory] = useState([]);

  // Data state
  const { data: workersData } = useWorkers({}, { enabled: !!user });
  const { data: jobsitesData } = useJobsites({}, { enabled: !!user });

  const templates = useMemo(() => COMMAND_TEMPLATES, []);
  const quickActions = useMemo(() => COMMAND_QUICK_ACTIONS, []);

  const entities = useMemo(() => {
    const rawWorkers = workersData?.workers || workersData?.data?.workers || [];
    const rawSites = jobsitesData?.data?.jobsites || jobsitesData?.jobsites || jobsitesData || [];

    const workers = Array.isArray(rawWorkers)
      ? rawWorkers.map((membership) => {
        const userInfo = membership?.userId || membership?.user || membership;
        const firstName = userInfo?.firstName || '';
        const lastName = userInfo?.lastName || '';
        const fullName = userInfo?.name || `${firstName} ${lastName}`.trim();
        return {
          id: membership?._id || userInfo?._id || userInfo?.id,
          name: fullName || 'Unnamed Worker',
          role: membership?.role || userInfo?.role || 'Worker',
          site: membership?.primaryJobsite?.name || membership?.primaryJobsite?.projectName || '',
        };
      })
      : [];

    const projects = Array.isArray(rawSites)
      ? rawSites.map((site) => ({
        id: site?._id || site?.id,
        name: site?.name || site?.projectName || 'Unnamed Site',
        type: 'project',
        site: site?.address?.city || site?.location?.city || '',
      }))
      : [];

    return {
      workers,
      projects,
      crews: [],
      sites: [],
    };
  }, [workersData, jobsitesData]);

  const [recentCommands, setRecentCommands] = useState(() => safeParseStoredCommands());
  const [savedSearches] = useState([]);

  useEffect(() => {
    persistRecentCommands(recentCommands);
  }, [recentCommands]);

  // Update input type when input changes
  useEffect(() => {
    if (input.trim()) {
      setInputType(detectInputType(input));
    } else {
      setInputType('search');
    }
  }, [input]);

  // Generate suggestions based on current input
  const suggestions = useMemo(() => {
    return generateSuggestions(input, recentCommands, templates, entities);
  }, [input, recentCommands, templates, entities]);

  // All suggestion items flattened for navigation
  const allSuggestionItems = useMemo(() => {
    return [
      ...suggestions.templates.map((t) => ({ ...t, suggestionType: 'template' })),
      ...suggestions.entities.map((e) => ({ ...e, suggestionType: 'entity' })),
      ...suggestions.recent.map((r) => ({ ...r, suggestionType: 'recent' })),
    ];
  }, [suggestions]);

  const visibleSearchResults = useMemo(
    () => flattenVisibleSearchResults(results),
    [results]
  );

  // Open command bar
  const open = useCallback(() => {
    setIsOpen(true);
    setInput('');
    setSelectedIndex(0);
    setPreview(null);
    setResults(null);
  }, []);

  // Close command bar
  const close = useCallback(() => {
    setIsOpen(false);
    setInput('');
    setSelectedIndex(0);
    setPreview(null);
    setResults(null);
  }, []);

  // Toggle command bar
  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, open, close]);

  // Handle input change
  const handleInputChange = useCallback((value) => {
    setInput(value);
    setSelectedIndex(0);
    setPreview(null);
    setResults(null);
  }, []);

  // Navigate suggestions, or visible search results when a search is showing
  const navigateUp = useCallback(() => {
    const length = results ? visibleSearchResults.length : allSuggestionItems.length;
    if (!length) return;
    setSelectedIndex((prev) => (prev > 0 ? prev - 1 : length - 1));
  }, [results, visibleSearchResults.length, allSuggestionItems.length]);

  const navigateDown = useCallback(() => {
    const length = results ? visibleSearchResults.length : allSuggestionItems.length;
    if (!length) return;
    setSelectedIndex((prev) => (prev < length - 1 ? prev + 1 : 0));
  }, [results, visibleSearchResults.length, allSuggestionItems.length]);

  // Execute command or search
  const execute = useCallback(async (command = null) => {
    const queryToExecute = command || input;
    if (!queryToExecute.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const type = detectInputType(queryToExecute);
      let parsed = null;
      let nextResults = null;

      if (type === 'command') {
        parsed = parseCommand(queryToExecute, templates);

        if (parsed?.template?.route && onNavigate) {
          onNavigate(parsed.template.route, { query: queryToExecute });
          const navigationEntry = {
            id: `rc-${Date.now()}`,
            query: queryToExecute,
            timestamp: 'just now',
            status: 'navigation',
          };
          setRecentCommands((prev) => [navigationEntry, ...prev.slice(0, 19)]);
          close();
          return;
        }

        const previewResult = buildPreview(queryToExecute, parsed);
        setPreview({
          ...previewResult,
          command: queryToExecute,
          parsed,
        });
      } else {
        const response = await fetchGlobalSearch({ q: queryToExecute, limit: 5 });
        if (!response?.success || !response?.data) {
          throw new Error('Global search returned an invalid response');
        }

        nextResults = {
          query: response.data.query,
          totalResults: response.data.totalResults,
          results: response.data.results,
          groups: response.data.groups,
        };
        setResults(nextResults);
        setSelectedIndex(0);
      }

      const newRecentCommand = {
        id: `rc-${Date.now()}`,
        query: queryToExecute,
        timestamp: 'just now',
        status: type === 'command' ? 'queued' : 'success',
        resultCount: nextResults?.totalResults,
      };
      setRecentCommands((prev) => [newRecentCommand, ...prev.slice(0, 19)]);

    } catch (err) {
      setError(err.message || 'Failed to execute command');
    } finally {
      setLoading(false);
    }
  }, [input, templates, onNavigate, close]);

  const activateSelectedResult = useCallback(() => {
    const item = visibleSearchResults[selectedIndex];
    if (!item || !onNavigate) return false;
    onNavigate(item.route, {
      id: item.id,
      resultType: item.resultType,
      metadata: item.metadata || {},
    });
    close();
    return true;
  }, [visibleSearchResults, selectedIndex, onNavigate, close]);

  const selectSuggestion = useCallback((suggestion) => {
    if (suggestion.suggestionType === 'template') {
      setInput(suggestion.examples?.[0] || suggestion.pattern);
    } else if (suggestion.suggestionType === 'entity') {
      setInput((prev) => `${prev} ${suggestion.name}`);
    } else if (suggestion.suggestionType === 'recent') {
      setInput(suggestion.query);
    }
    setSelectedIndex(0);
  }, []);

  // Confirm preview action
  const confirmPreview = useCallback(async (action = 'execute') => {
    if (!preview) return;
    if (preview.canExecute === false) {
      setError('Command execution is not wired to production yet.');
      return;
    }

    setLoading(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 300));
      setResults({
        query: preview.command || input,
        message: 'Command queued for execution.',
      });

      setPreview(null);
    } catch (err) {
      setError(err.message || 'Failed to execute command');
    } finally {
      setLoading(false);
    }
  }, [preview, input]);

  // Cancel preview
  const cancelPreview = useCallback(() => {
    setPreview(null);
  }, []);

  // Clear results
  const clearResults = useCallback(() => {
    setResults(null);
  }, []);

  // Keyboard shortcut handler (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd+K or Ctrl+K to toggle (lowercase — matches deleted CommandPalette contract)
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        toggle();
        return;
      }

      if (!isOpen) return;

      // Escape to close
      if (e.key === 'Escape') {
        e.preventDefault();
        if (preview) {
          cancelPreview();
        } else if (results) {
          clearResults();
        } else {
          close();
        }
        return;
      }

      // Arrow navigation
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        navigateUp();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        navigateDown();
        return;
      }

      // Enter to execute, select a visible search result, or select a suggestion.
      // CommandInput already handles Enter (preventDefault + stopPropagation).
      // Respect defaultPrevented so a leaked bubble cannot double-activate.
      if (e.key === 'Enter') {
        if (e.defaultPrevented) return;
        e.preventDefault();
        if (preview) {
          confirmPreview();
        } else if (results && visibleSearchResults.length) {
          activateSelectedResult();
        } else if (allSuggestionItems[selectedIndex]) {
          selectSuggestion(allSuggestionItems[selectedIndex]);
        } else {
          execute();
        }
        return;
      }

      // Tab to autocomplete
      if (e.key === 'Tab' && allSuggestionItems[selectedIndex]) {
        e.preventDefault();
        selectSuggestion(allSuggestionItems[selectedIndex]);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    isOpen,
    toggle,
    close,
    preview,
    results,
    cancelPreview,
    clearResults,
    navigateUp,
    navigateDown,
    confirmPreview,
    allSuggestionItems,
    visibleSearchResults,
    selectedIndex,
    selectSuggestion,
    activateSelectedResult,
    execute,
  ]);

  return {
    // State
    isOpen,
    input,
    inputType,
    loading,
    error,
    preview,
    results,
    selectedIndex,

    // Data
    suggestions,
    allSuggestionItems,
    visibleSearchResults,
    templates,
    entities,
    recentCommands,
    quickActions,
    savedSearches,

    // Actions
    open,
    close,
    toggle,
    handleInputChange,
    navigateUp,
    navigateDown,
    execute,
    selectSuggestion,
    activateSelectedResult,
    confirmPreview,
    cancelPreview,
    clearResults,
  };
};

export default useCommandBar;
